"use client";

import React, { useState, useMemo } from "react";
import { NavigationProvider } from "./NavigationContext";
import { useLocale } from "next-intl";
import { usePathname } from "i18n/routing";
import darkTheme from "themes/dark";
import OverlayMenu from "../overlay";
import { NavContext } from "./nav-context";
import { Grid } from "components/layout/Grid";
import { NavigationWrapper } from "./style";
import { FILES } from "i18n/constants";
import { useTranslations } from "i18n";
import type { AppTheme } from "themes/global";
import type { NavLinkKey } from "./constants";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";
import { getMainNavigationLinks } from "./navigation-links";
import NavigationContainer from "./components/NavigationContainer";
import SubMenuContainer from "./components/SubMenuContainer";
import GeminiInstitutionsBanner from "../banners/gemini-institutions-banner";
import { isTouchDevice } from "./components/MenuItemWithDropdown";
import { usePathnameChecks } from "hooks/usePathnameChecks";

interface Props {
  hideNavigationLinks?: boolean;
  theme?: AppTheme;
  rightItems?: React.ReactNode;
}

// Base links that don't have overlays by default
const BASE_NO_OVERLAY: NavLinkKey[] = [
  "Prices",
  "Staking",
  "Perpetuals",
  "TokenizedStocks"
];

const MainNav = ({
  theme = darkTheme,
  hideNavigationLinks = false,
  rightItems
}: Props) => {
  const pathname = usePathname();
  const locale = useLocale();
  const { country } = useCountryStateCookies();
  const t = useTranslations(FILES.COMMON);

  const [activeMenu, setActiveMenu] = useState<NavLinkKey | null>(null);

  const { isGoogleSearchPath, isHighConversionPath, isPartnerPath } =
    usePathnameChecks();

  // Memoize base navigation links to ensure stable reference
  const navigationLinks = useMemo(
    () =>
      getMainNavigationLinks({
        t,
        locale,
        countryCode: country
      }),
    [t, locale, country]
  );

  // Check if active menu should display overlay
  const hasOverlay =
    activeMenu !== null && !BASE_NO_OVERLAY.includes(activeMenu);

  // Create navigation provider value
  const navigationValue = {
    activeMenu,
    setActiveMenu,
    isCloseButtonActive: false,
    setIsCloseButtonActive: () => {}
  };

  return (
    <NavigationProvider value={navigationValue}>
      <NavigationWrapper
        theme={theme}
        noPadding
        isOpen={hasOverlay}
        onMouseLeave={() => {
          if (!isTouchDevice()) setActiveMenu(null);
        }}
      >
        <NavContext.Provider
          value={{
            handleCloseMenu: () => setActiveMenu(null)
          }}
        >
          <Grid smSpacing={0}>
            <OverlayMenu
              isOpen={hasOverlay}
              handleClose={() => setActiveMenu(null)}
            >
              <NavigationContainer
                theme={theme}
                hideNavigationLinks={hideNavigationLinks}
                hidePathNavigationLinks={
                  isGoogleSearchPath || isPartnerPath || isHighConversionPath
                }
                isGoogleSearchPage={isGoogleSearchPath}
                navigationLinks={navigationLinks}
                rightItems={rightItems}
              />
            </OverlayMenu>
          </Grid>

          {!hasOverlay && <GeminiInstitutionsBanner path={pathname} />}

          <SubMenuContainer hasOverlay={hasOverlay} />
        </NavContext.Provider>
      </NavigationWrapper>
    </NavigationProvider>
  );
};

export default MainNav;
